import type { ActionType } from '~/@types/account';
import type { CustomerQuery } from '~/graphql/generated-customer';

const excludedCountriesFromSort = ['AT', 'DE', 'CH', 'BE', 'CZ', 'DK', 'FR', 'HU', 'IT', 'LU', 'NL', 'PL', 'RO', 'SE', 'SK', 'US'];
export const useCountries = (language: string) => {
    const regionNames = new Intl.DisplayNames([language], { type: 'region' });
    return [
        ...excludedCountriesFromSort,
        'AC',
        'AD',
        'AE',
        'AF',
        'AG',
        'AI',
        'AL',
        'AM',
        'AN',
        'AO',
        'AR',
        'AU',
        'AW',
        'AX',
        'AZ',
        'BA',
        'BB',
        'BD',
        'BF',
        'BG',
        'BH',
        'BI',
        'BJ',
        'BL',
        'BM',
        'BN',
        'BO',
        'BQ',
        'BR',
        'BS',
        'BT',
        'BV',
        'BW',
        'BY',
        'BZ',
        'CA',
        'CC',
        'CD',
        'CF',
        'CG',
        'CI',
        'CK',
        'CL',
        'CM',
        'CN',
        'CO',
        'CR',
        'CU',
        'CV',
        'CW',
        'CX',
        'CY',
        'DJ',
        'DM',
        'DO',
        'DZ',
        'EC',
        'EE',
        'EG',
        'EH',
        'ER',
        'ES',
        'ET',
        'FI',
        'FJ',
        'FK',
        'FO',
        'GA',
        'GB',
        'GD',
        'GE',
        'GF',
        'GG',
        'GH',
        'GI',
        'GL',
        'GM',
        'GN',
        'GP',
        'GQ',
        'GR',
        'GS',
        'GT',
        'GW',
        'GY',
        'HK',
        'HM',
        'HN',
        'HR',
        'HT',
        'ID',
        'IE',
        'IL',
        'IM',
        'IN',
        'IO',
        'IQ',
        'IR',
        'IS',
        'JE',
        'JM',
        'JO',
        'JP',
        'KE',
        'KG',
        'KH',
        'KI',
        'KM',
        'KN',
        'KP',
        'KR',
        'KW',
        'KY',
        'KZ',
        'LA',
        'LB',
        'LC',
        'LI',
        'LK',
        'LR',
        'LS',
        'LT',
        'LV',
        'LY',
        'MA',
        'MC',
        'MD',
        'ME',
        'MF',
        'MG',
        'MK',
        'ML',
        'MM',
        'MN',
        'MO',
        'MQ',
        'MR',
        'MS',
        'MT',
        'MU',
        'MV',
        'MW',
        'MX',
        'MY',
        'MZ',
        'NA',
        'NC',
        'NE',
        'NF',
        'NG',
        'NI',
        'NO',
        'NP',
        'NR',
        'NU',
        'NZ',
        'OM',
        'PA',
        'PE',
        'PF',
        'PG',
        'PH',
        'PK',
        'PM',
        'PN',
        'PS',
        'PT',
        'PY',
        'QA',
        'RE',
        'RS',
        'RU',
        'RW',
        'SA',
        'SB',
        'SC',
        'SD',
        'SG',
        'SH',
        'SI',
        'SJ',
        'SL',
        'SM',
        'SN',
        'SO',
        'SR',
        'SS',
        'ST',
        'SV',
        'SX',
        'SY',
        'SZ',
        'TA',
        'TC',
        'TD',
        'TF',
        'TG',
        'TH',
        'TJ',
        'TK',
        'TL',
        'TM',
        'TN',
        'TO',
        'TR',
        'TT',
        'TV',
        'TW',
        'TZ',
        'UA',
        'UG',
        'UM',
        'UY',
        'UZ',
        'VA',
        'VC',
        'VE',
        'VG',
        'VN',
        'VU',
        'WF',
        'WS',
        'XK',
        'YE',
        'YT',
        'ZA',
        'ZM',
        'ZW',
    ]
        .map((countryCode) => {
            return {
                value: countryCode,
                label: regionNames.of(countryCode) || countryCode,
            };
        })
        .sort((a, b) => {
            if (excludedCountriesFromSort.includes(a.value) || excludedCountriesFromSort.includes(b.value)) return 0;
            return a.label.localeCompare(b.label);
        });
};

export const useAccount = () => {
    const { t, locale } = useLocales();
    const runtimeConfig = useRuntimeConfig();
    const localPath = useLocalePathWoom();

    const createA11yLabel = (prefix?: string, action?: ActionType, postfix?: string) => {
        if (!prefix || !action || !postfix) return '';
        return t(`${prefix}.${action}`, { context: postfix });
    };

    const accountEnabled = computed(() => {
        const rtc = runtimeConfig.public.accountSectionEnabled;
        return rtc.includes(locale.value);
    });

    const fetchCustomer = async () => {
        // https://nuxt.com/docs/getting-started/data-fetching#client-only-fetching
        return await useFetch<CustomerQuery['customer']>(localPath('/customer'), {
            method: 'POST',
            key: 'customer',
            lazy: true,
            server: false,
        });
    };

    return {
        createA11yLabel,
        accountEnabled,
        fetchCustomer,
    };
};
